import { Minus, PackageCheck, Plus, ShoppingCart } from 'lucide-react'
import { MdCurrencyExchange, MdLock } from 'react-icons/md'

import { Button } from '@/modules/shared/components/ui/button'
import { Card, CardContent } from '@/modules/shared/components/ui/card'
import { Separator } from '@/modules/shared/components/ui/separator'
import { cn } from '@/modules/shared/lib/utils'

import { ProductCardStatusComponent } from '../components/product-card-status.component'
import type { TProductCardInterface } from '../types/product-card-interface.type'
import { useEffect, useReducer, useRef, useState } from 'react'
import { useOrderFormStore, useOrderFormStoreActions } from '@/modules/order/stores/order-form.store'
import { useControllerProductHook } from '../hooks/use-controller-product.hook'
import { useGetParamsLayoutProductDetailsHook } from '../hooks/use-get-params-layout-product-details.hook'
import { Input } from '@/modules/shared/components/ui/input'
import { useForm } from 'react-hook-form'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
} from '@/modules/shared/components/ui/form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { useCalculationProductDetails } from '../hooks/use-calculation-product-details-form.hook'
import { getTextColorLL } from '../utils/get-text-color-ll.util'
import { moneyFormatter } from '@/modules/shared/utils/money-formatter'
import useOrderFormParams from '@/modules/order/hooks/use-order-form-params.hook'
import { ProductCardFooterComponent } from '../components/product-card-footer.component'
import { PromotionBadge } from '../components/PromotionBadge'
import { useProductStockLimit } from '../hooks/use-product-stock-limit.hook'

const schema = z.object({
	unitaryValue: z.coerce.number().min(0, 'Required'),
	quantity: z.coerce.number().min(0, 'Quantidade deve ser maior ou igual a 0').max(999999, 'Quantidade muito alta'),
	fixedValue: z.boolean().default(false),
	discount: z.number().optional(),
})
type ProductCardForm = z.infer<typeof schema>

const truncateToPrecision = (value: number, precision = 2): number => {
	const valueStr = value.toString();
	const decimalIndex = valueStr.indexOf('.');

	if (decimalIndex === -1) {
		return value;
	}

	const currentDecimalPlaces = valueStr.length - decimalIndex - 1;

	if (currentDecimalPlaces <= precision) {
		return value;
	}

	const truncatedStr = valueStr.substring(0, decimalIndex + precision + 1);
	const truncated = Number.parseFloat(truncatedStr);

	return truncated;
};
const isNearlyEqual = (a: number, b: number, tolerance = 0.01): boolean => {
	return Math.abs(a - b) < tolerance;
};

const formatMoneyWithTruncate = (value: number): string => {
	const truncated = truncateToPrecision(value, 2);
	return moneyFormatter.format(truncated);
};

const formatNumberWithTruncate = (value: number, precision = 2): string => {
	return truncateToPrecision(value, precision).toFixed(precision);
};

const formatPercentageWithTruncate = (value: number, precision = 2): string => {
	return truncateToPrecision(value, precision).toString().replace('.', ',');
};

export function ProductCardInterface({
	productCardInterfaceController,
	layoutType,
	quantityRef,
	isCalculatingOrder,
	inputSearchRef,
}: TProductCardInterface) {
	const [localTotalValue, setLocalTotalValue] = useState(undefined)
	const [unitaryBase, setUnitaryBase] = useState(0)
	const [quantityError, setQuantityError] = useState(false)
	const [discountValue, setDiscountValue] = useState('')

	const { getParamsLayout } = useGetParamsLayoutProductDetailsHook()
	const { utilizaValorSugerido } = useOrderFormParams()

	const unitaryValueParams = getParamsLayout('unitaryValueDetails')
	const discountParams = getParamsLayout('discountDetails')

	const { paramsLayoutOrderForm } = useOrderFormStore()

	const fieldHeader = paramsLayoutOrderForm?.geral?.find(
		(item) => item.campo === 'field2',
	)

	const {
		action: {
			onOpenModalProductImage,
			onOpenModalDetailsOfProduct,
			onDisablePromotion,
			onActivatePromotion,
			onDecrementQuantityOfProduct,
			onIncrementQuantityOfProduct,
			onCalculateDateLastBuyProduct,
			onChangeDefaultTabsProductDetails,
		},
		state: {
			product,
			isVisibleFixedValue,
			deferredQuantity,
			isActiveButton,
			isViewOrder,
		},
		params: { availableStock },
	} = productCardInterfaceController

	const { limitaQuantidadePorEstoque, limitQuantityToStock, stockWarning } = useProductStockLimit(product)

	const {
		produto: { imagem, referenciaAfere, descricao, qtdEstoque },
		tabelaPrecoProduto: { valorPromocao, qtdPromocao },
		flagPromocao,
		valorPromocaoDesconto,
		quantidade,
		valorUnitario,
		valorTotalItem,
		statusLl,
		flagPrecoFixo,
		ultimoPedido,
		j_Plucro,
		porcMaxDesconto,
	} = product

	const formattedPromotionValue = formatMoneyWithTruncate(valorPromocao)
	const formattedPromotionValueWithDiscount = formatMoneyWithTruncate(valorPromocaoDesconto)

	const formattedLastBuyText =
		ultimoPedido?.quantidade > 0
			? `Comprou ${ultimoPedido.quantidade} ${onCalculateDateLastBuyProduct()}`
			: ''

	const getStatusColor = (status: number) => {
		switch (status) {
			case 4:
				return 'bg-amber-500  text-white'
			case 6:
				return 'bg-red-500  text-white'
			case 12:
				return 'bg-green-500 text-white'
			default:
				return 'bg-green-500 text-white'
		}
	}

	const { calcDiscountWithUnitaryValue, calcUnitaryValueWithDiscount } =
		useCalculationProductDetails(product)

	const { setIsCalculationOrder } = useOrderFormStoreActions()
	const { addOrUpdateProductOnOrder } = useControllerProductHook(product)
	const { jsonOrderForm } = useOrderFormStore()

	// Valida se a data de emissão está dentro do período da promoção
	const isPromotionDateValid = () => {
		const dataEmissao = jsonOrderForm?.dataEmissao ? new Date(jsonOrderForm.dataEmissao) : new Date();
		const dataInicioPromocao = product?.tabelaPrecoProduto?.dataInicioPromocao
			? new Date(product?.tabelaPrecoProduto?.dataInicioPromocao)
			: null;
		const dataFimPromocao = product?.tabelaPrecoProduto?.dataFimPromocao
			? new Date(product?.tabelaPrecoProduto?.dataFimPromocao)
			: null;

		// Se não houver datas de promoção definidas, não mostra a promoção
		if (!dataInicioPromocao || !dataFimPromocao) {
			return false;
		}

		// Verifica se a data de emissão está entre o início e fim da promoção
		return dataEmissao >= dataInicioPromocao && dataEmissao <= dataFimPromocao;
	};

	const hasPromotion = valorPromocao > 0 && isPromotionDateValid();

	const isQuantityValidForPromotion = (currentQuantity: number) => {
		return currentQuantity >= qtdPromocao
	}

	const form = useForm<ProductCardForm>({
		resolver: zodResolver(schema),
		defaultValues: {
			quantity: product.quantidade,
			unitaryValue: utilizaValorSugerido && product.quantidade === 0 ? product.valorSugerido : (jsonOrderForm.fkRegra.toString() === '1' ? product.valorUnitarioBase : product.valorUnitario),
			fixedValue: product.flagPrecoFixo,
			discount: product.porcDescontoTabela
		},
	})

	const canActivatePromotion = () => {
		if (jsonOrderForm.cliente?.permitePromocao) return true
		const currentQuantity = form.watch('quantity') || quantidade
		return hasPromotion && isQuantityValidForPromotion(currentQuantity)
	}

	const deferredQuantityInputRef = quantityRef

	const formatPercentage = (value: string) => {
		let cleanValue = value.replace(/[^\d.,]/g, '')
		cleanValue = cleanValue.replace(',', '.')

		const parts = cleanValue.split('.')
		if (parts.length > 2) {
			cleanValue = `${parts[0]}.${parts.slice(1).join('')}`
		}

		if (parts[1] && parts[1].length > 4) {
			cleanValue = `${parts[0]}.${parts[1].substring(0, 4)}`
		}

		return cleanValue
	}

	const validateMaxDiscount = (discountValue: number) => {
		if (!porcMaxDesconto || porcMaxDesconto === 0) {
			return discountValue <= 100
		}
		return discountValue <= porcMaxDesconto
	}

	const handleDiscountChange = (newDiscountValue: string) => {
		const formattedValue = formatPercentage(newDiscountValue)
		const discountNumber = Number(formattedValue.replace(',', '.'))

		if (discountNumber < 0) return

		if (!validateMaxDiscount(discountNumber)) {
			const maxDiscount = porcMaxDesconto && porcMaxDesconto > 0 ? porcMaxDesconto : 100
			toast.info(`Desconto máximo permitido: ${maxDiscount}%`)
			return
		}

		setDiscountValue(formattedValue)

		const { newUnitaryValue, newUnitaryValueBase } =
			calcUnitaryValueWithDiscount(discountNumber)

		setUnitaryBase(newUnitaryValueBase ?? 0)

		form.setValue('unitaryValue', newUnitaryValue, { shouldValidate: true })
		form.setValue('fixedValue', discountNumber !== 0, { shouldValidate: true })
		form.setValue('discount', discountNumber, { shouldValidate: true })

		setValue(formatMoneyWithTruncate(newUnitaryValue))
	}

	useEffect(() => {
		const initialUnitaryValue = utilizaValorSugerido && product.quantidade === 0 ? product.valorSugerido : (jsonOrderForm.fkRegra.toString() === '1' ? product.valorUnitarioBase : product.valorUnitario)

		form.reset({
			discount: product.porcDescontoTabela,
			unitaryValue: initialUnitaryValue,
			fixedValue: product.flagPrecoFixo,
			quantity: product.quantidade,
		})

		setValue(formatMoneyWithTruncate(initialUnitaryValue))

		if (layoutType === 'grid') {
			setDiscountValue(product.porcDescontoTabela ? formatPercentageWithTruncate(product.porcDescontoTabela, 2) : '0')
		}

	}, [product.quantidade, product.valorUnitario, product.flagPrecoFixo, product.porcDescontoTabela, product, layoutType])

	const isReadOnly = unitaryValueParams && !unitaryValueParams.visible
	const shouldUseRef = !unitaryValueParams || unitaryValueParams.visible === true
	const deferredUnitaryValueRef = useRef<HTMLInputElement>(null)
	const discountInputRef = useRef<HTMLInputElement>(null)

	const watchQuantity = form.watch('quantity')
	const watchUnitaryValue = form.watch('unitaryValue')

	const handleAddProduct = (data: ProductCardForm) => {
		if (
			form.getFieldState('quantity').error ||
			form.getFieldState('unitaryValue').error
		) {
			toast.info('Quantidade ou valor unitário inválidos')
			return;
		}

		const finalQuantity = limitQuantityToStock(Math.max(0, Number(data.quantity)));
		form.setValue('quantity', finalQuantity);
		if (limitaQuantidadePorEstoque && jsonOrderForm.operacao.flagValidaQtdEmbalagem && finalQuantity % product.produto.qtdeEmbalagem !== 0) {
			toast.info('Quantidade deve ser múltipla da quantidade de embalagem');
			setQuantityError(true);
			return;
		}
		setQuantityError(false);

		addOrUpdateProductOnOrder({
			quantidade: finalQuantity,
			valorUnitario: data.unitaryValue,
			flagPrecoFixo: data.fixedValue,
			porcDescontoTabela: data.discount,
			valorUnitarioBase: unitaryBase,
		})
	}

	const handleKeyUpOrBlurQuantity = (
		e?: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>
	) => {
		if (e && "key" in e && e.key !== 'Enter' && e.key !== 'Tab') return;

		if (isViewOrder || isCalculatingOrder) return;

		if (form.getValues('quantity') < 0) {
			form.setValue('quantity', 0);
			form.setValue("discount", 0);
			setDiscountValue('0');
			return;
		}

		form.setValue('quantity', limitQuantityToStock(Number(form.getValues('quantity'))));
		const result = schema.safeParse(form.getValues());
		if (!result.success) {
			toast.info('Quantidade ou valor unitário inválidos');
			return;
		}
		if (jsonOrderForm.operacao.flagValidaQtdEmbalagem && result.data.quantity % product.produto.qtdeEmbalagem !== 0) {
			toast.info("Quantidade deve ser múltipla da quantidade de embalagem")
			setQuantityError(true);
			return;
		}
		setQuantityError(false);
		form.clearErrors(['quantity', 'unitaryValue']);

		// Salva antes de uma troca de aba poder desmontar o formulário.
		setIsCalculationOrder(true);
		handleAddProduct(result.data);
		if (e?.type === 'blur') return;

		if (shouldUseRef && deferredUnitaryValueRef.current) {
			deferredUnitaryValueRef.current.select();
			deferredUnitaryValueRef.current.focus();
			return;
		}

		inputSearchRef?.current?.select();
		inputSearchRef?.current?.focus();
	};

	const focusProductSearch = () => {
		inputSearchRef?.current?.focus();
		inputSearchRef?.current?.select();
	};

	const handleQuantityButton = (increment: boolean) => {
		if (!limitaQuantidadePorEstoque) {
			if (increment) onIncrementQuantityOfProduct();
			else onDecrementQuantityOfProduct();
			return;
		}
		if (isViewOrder || isCalculatingOrder) return;
		const step = product.produto.qtdeEmbalagem ?? 1;
		const nextQuantity = limitQuantityToStock(Math.max(0, Number(form.getValues('quantity') || 0) + (increment ? step : -step)));
		form.setValue('quantity', nextQuantity);
		if (jsonOrderForm.operacao.flagValidaQtdEmbalagem && nextQuantity % step !== 0) {
			toast.info('Quantidade deve ser múltipla da quantidade de embalagem');
			setQuantityError(true);
			return;
		}
		setQuantityError(false);
		if (nextQuantity === product.quantidade) return;
		if (increment && nextQuantity > 0) onIncrementQuantityOfProduct(nextQuantity);
		else onDecrementQuantityOfProduct(nextQuantity);
	};

	const handleCommitUnitaryValue = (
		e?: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>
	) => {
		if (e && "key" in e) {
			if (e.key !== 'Enter') return;
			e.preventDefault();
		}

		if (isViewOrder || isReadOnly || isCalculatingOrder) return;

		// Grava antes do próximo clique para os botões de quantidade usarem o preço atual.
		form.setValue('quantity', limitQuantityToStock(Number(form.getValues('quantity'))));
		const result = schema.safeParse(form.getValues());
		if (!result.success) {
			toast.info('Quantidade ou valor unitário inválidos');
			return;
		}
		const data = result.data;
		form.clearErrors(['quantity', 'unitaryValue']);

		if (jsonOrderForm.operacao.flagValidaQtdEmbalagem && data.quantity % product.produto.qtdeEmbalagem !== 0) {
			setQuantityError(true);
			return;
		}
		setQuantityError(false);

		const currentUnitaryValue = jsonOrderForm.fkRegra.toString() === '1'
			? product.valorUnitarioBase
			: product.valorUnitario;
		if (data.unitaryValue === currentUnitaryValue && data.quantity === product.quantidade) {
			if (e && "key" in e) focusProductSearch();
			return;
		}

		const { newUnitaryValue, newUnitaryValueBase } = calcDiscountWithUnitaryValue(data.unitaryValue);

		setUnitaryBase(newUnitaryValueBase);

		if (layoutType === 'grid') {
			const currentDiscount = form.getValues('discount') || 0;

			if (isNearlyEqual(currentDiscount, newUnitaryValue, 0.01)) {
				form.setValue('discount', currentDiscount);
				setDiscountValue(formatPercentageWithTruncate(currentDiscount, 2));
			} else {
				form.setValue('discount', newUnitaryValue);
				setDiscountValue(formatPercentageWithTruncate(newUnitaryValue, 2));
			}
		} else {
			form.setValue('discount', newUnitaryValue);
		}

		if (utilizaValorSugerido) {
			if (data.unitaryValue !== product.valorSugerido) {
				form.setValue('fixedValue', true);
			}
		} else {
			if (
				data.unitaryValue !==
				(jsonOrderForm.fkRegra.toString() === '1'
					? product.valorUnitarioBase
					: product.valorUnitario)
			) {
				form.setValue('fixedValue', true);
			}
		}

		setIsCalculationOrder(true);
		handleAddProduct({
			...data,
			discount: form.getValues('discount'),
			fixedValue: form.getValues('fixedValue'),
		});
		setValue(formatMoneyWithTruncate(data.unitaryValue));

		if (e && "key" in e) focusProductSearch();
	};

	const handleKeyUpOrBlurDiscount = (
		e?: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>
	) => {
		if (e && "key" in e && e.key !== 'Enter' && e.key !== 'Tab') return;

		if (isCalculatingOrder) return;

		setIsCalculationOrder(true);
		form.handleSubmit(handleAddProduct)();

		inputSearchRef?.current?.focus();
		inputSearchRef?.current?.select();
	};

	const initialValue = form.getValues().unitaryValue
		? formatMoneyWithTruncate(form.getValues().unitaryValue)
		: ''

	const [value, setValue] = useReducer((_: any, next: string) => {
		const digits = next.replace(/\D/g, '')
		const realValue = Number(digits) / 100

		return formatMoneyWithTruncate(realValue)
	}, initialValue)

	const handleChangeUnitaryValue = (
		realChangeFn: (value: number) => void,
		formattedValue: string,
	) => {
		const digits = formattedValue.replace(/\D/g, '')
		const realValue = Number(digits) / 100
		realChangeFn(realValue)
	}

	useEffect(() => {
		if (watchQuantity <= 0) {
			form.setValue('quantity', 0);
			//onDisablePromotion()
			return;
		}
		if (flagPromocao && valorPromocao > 0 && watchQuantity < qtdPromocao) {
			if (jsonOrderForm.cliente?.permitePromocao) {
				if (watchQuantity <= 0) {
					onDisablePromotion()
				}
			} else {

				onDisablePromotion()
			}
		}
	}, [watchQuantity, flagPromocao, valorPromocao, qtdPromocao, form])

	useEffect(() => {
		const safeQuantity = Math.max(0, watchQuantity);

		if (jsonOrderForm.operacao.flagValidaQtdEmbalagem && safeQuantity % product.produto.qtdeEmbalagem !== 0) {
			setLocalTotalValue(0)
			return
		}

		const calculatedTotal = safeQuantity * watchUnitaryValue;
		setLocalTotalValue(calculatedTotal)
	}, [watchQuantity, watchUnitaryValue])

	if (layoutType === 'grid') {
		return (
			<div className="w-full h-full">
				<Card
					className={cn(
						'w-full h-full border-gray-300',
						product.quantidade > 0 &&
						'border-green-400 shadow-xs',
						hasPromotion && 'border-gray-300',
						product.quantidade > 0 && 'border-gray-300'
					)}
				>
					<CardContent className="py-2 px-4 flex flex-col flex-1 h-full relative">

						{hasPromotion && (
							<PromotionBadge
								showMinQuantity={!jsonOrderForm.cliente?.permitePromocao}
								valorPromocao={valorPromocao}
								flagPromocao={flagPromocao}
								canActivatePromotion={canActivatePromotion}
								onActivatePromotion={onActivatePromotion}
								onDisablePromotion={onDisablePromotion}
								qtdPromocao={qtdPromocao}
								formattedPromotionValue={formattedPromotionValue}
								formattedPromotionValueWithDiscount={formattedPromotionValueWithDiscount}
								product={product}
								toast={toast}
							/>
						)}


						{product.quantidade > 0 && (
							<div className={cn('flex items-center justify-end', fieldHeader?.fieldName !== 'j_Plucro' && 'mt-3')}>
								{fieldHeader?.fieldName === 'j_Plucro' && (
									<p className={cn('pr-8 text-sm font-medium', getTextColorLL(statusLl))}>
										{j_Plucro}
									</p>
								)}

								<div
									className={cn(
										'w-7 flex h-7 items-center justify-center rounded-full absolute right-2',
										getStatusColor(Number(statusLl)),
									)}
								>
									<ShoppingCart size={16} />
								</div>
							</div>
						)}

						<div className="flex flex-row flex-1 mt-auto justify-between relatie">
							<div className="flex flex-1 flex-col justify-center">
								<div className="flex flex-col">
									<div
										onKeyUp={(e) => {
											if (e.key === 'Enter') {
												onOpenModalProductImage()
											}
										}}
										className="cursor-pointer flex flex-col items-center justify-center flex-1 mt-3"
										onClick={onOpenModalProductImage}
									>
										<div className='flex flex-col justify-between mr-2'>
											<img
												src={imagem || '/sem-foto.jpeg'}
												alt="Imagem do produto"
												className="img object-scale-down justify-center w-32 h-32 md:w-36 md:h-36"
											/>
										</div>

										<div
											onKeyUp={(e) => {
												if (e.key === 'Enter') {
													e.stopPropagation()
													onChangeDefaultTabsProductDetails('dados')
													onOpenModalDetailsOfProduct()
												}
											}}
											className="w-full cursor-pointer mb-2 text-xs"
											onClick={(e) => {
												e.stopPropagation()
												onChangeDefaultTabsProductDetails('dados')
												onOpenModalDetailsOfProduct()
											}}
										>
											<p className="font-semibold mb-2">
												{referenciaAfere} - {descricao}
											</p>



											{availableStock && (
												<div className="flex text-xs">
													<p className="mr-1">Qtd. Disponível:</p>
													<p>{qtdEstoque}</p>
												</div>
											)}
											{stockWarning && <p role="status" className="text-amber-700 text-xs">{stockWarning}</p>}

											<div className="block">


												{product?.produto?.familia && (
													<div className="flex text-xs">
														<p className="mr-1 flex gap-1">
															Família:{' '}
															<p className="font-normal">
																{product.produto.familia}
															</p>
														</p>
													</div>
												)}

												<div className="flex text-xs">
													<p className="mr-1  flex gap-1">
														Quant. Embalagem:{' '}
														<p className="font-normal">
															{product.produto.qtdeEmbalagem}
														</p>
													</p>
												</div>

												<div className="flex text-xs flex-row items-center">
													<p className="mr-1">Vr. Tabela:</p>
													<p className="font-semibold mr-2">
														{utilizaValorSugerido ? formatNumberWithTruncate(product.valorSugerido, 2) : formatNumberWithTruncate(product.valorTabela, 2)}
													</p>
													{product.tabelaPrecoConcessao.idTabelaPrecoxConcessao !== 0 && <p className='text-white rounded-md py-[2px] px-2 text-xs bg-green-500'>Concessão</p>}
												</div>
												{product.valorPromocao > 0 && (
													<div className="flex text-xs flex-row items-center">
														<p className="mr-1">Vr. Promoção s/ desconto:</p>
														{product.valorPromocao > 0 && <p className="font-semibold mr-2">{formatNumberWithTruncate(product.valorPromocao, 2)}</p>}
													</div>
												)}

												<div className="lg:h-4 flex items-end mt-2">
													{formattedLastBuyText && (
														<div
															onKeyUp={(e) => {
																if (e.key === 'Enter') {
																	e.stopPropagation()
																	onChangeDefaultTabsProductDetails('historico')
																	onOpenModalDetailsOfProduct()
																}
															}}
															className="flex text-amber-500 gap-1 text-[11px] flex-row items-center font-medium"
															onClick={(e) => {
																e.stopPropagation()
																onChangeDefaultTabsProductDetails('historico')
																onOpenModalDetailsOfProduct()
															}}
														>
															<div className="rounded-full  ">
																<PackageCheck size={14} className="text-amber-500" />
															</div>
															<p>{formattedLastBuyText}</p>
														</div>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div className="w-28 absolute -left-7 top-8">
								<div className="flex justify-center w-full text-center">
									{flagPrecoFixo && isVisibleFixedValue && (
										<MdLock size={18} className="mt-8" />
									)}
								</div>
							</div>
						</div>

						<Separator className="my-2" />

						<div className="space-y-2">
							<Form {...form}>
								<div className="flex gap-1 sm:gap-2 items-end">
									<div className="flex-1 min-w-0">
										<label htmlFor="quantity-input" className="block text-[9px] sm:text-[10px] font-medium text-gray-700 mb-1 text-center">
											Qtd
										</label>
										<FormField
											control={form.control}
											name="quantity"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input
															id="quantity-input"
															{...field}
															value={
																Number.isFinite(field.value) && field.value % 1 !== 0 && field.value.toString().split('.')[1]?.length > 2
																	? formatNumberWithTruncate(field.value, 2)
																	: field.value
															}
															autoComplete='off'
															disabled={isViewOrder}
															onFocus={(ev) => {
																ev.currentTarget.select()
															}}
															onChange={(e) => {
																const inputValue = e.target.value;
																if (inputValue === '') {
																	limitQuantityToStock(0);
																	field.onChange('');
																} else {
																	const value = Math.max(0, Number(inputValue) || 0);
																	field.onChange(limitQuantityToStock(value));
																}
															}}
															onBlur={(ev) => {
																const currentValue = field.value;
																if (currentValue === undefined || Number.isNaN(Number(currentValue))) {
																	field.onChange(0);
																} else {
																	const value = Math.max(0, Number(currentValue));
																	field.onChange(value);
																}
																field.onBlur();
																handleKeyUpOrBlurQuantity(ev);
															}}
															type="number"
															inputMode="numeric"
															min="0"
															onKeyUp={handleKeyUpOrBlurQuantity}
															ref={deferredQuantityInputRef}
															className={cn("h-7 sm:h-8 px-1 sm:px-2 bg-transparent border-0 border-b border-gray-300 rounded-none ring-0 text-center focus:ring-0 focus:border-blue-500 focus:outline-hidden text-xs font-semibold",
																quantityError && 'border-b-red-500 ')}
														/>
													</FormControl>
												</FormItem>
											)}
										/>
									</div>

									{discountParams?.visible !== false && (
										<div className="flex-1 min-w-0">
											<label htmlFor="discount-input" className="block text-[9px] sm:text-[10px] font-medium text-gray-700 mb-1 text-center">
												Desc
											</label>
											<div className="relative">
												<Input
													id="discount-input"
													autoComplete='off'
													disabled={isViewOrder || quantidade <= 0}
													value={discountValue}
													onChange={(e) => handleDiscountChange(e.target.value)}
													onKeyUp={handleKeyUpOrBlurDiscount}
													onBlur={handleKeyUpOrBlurDiscount}
													onFocus={(ev) => ev.currentTarget.select()}
													ref={discountInputRef}
													className="h-7 sm:h-8 px-1 sm:px-2 pr-4 sm:pr-5 bg-transparent border-0 border-b border-gray-300 rounded-none ring-0 text-center focus:ring-0 focus:border-blue-500 focus:outline-hidden text-xs font-semibold"
													placeholder="0"
												/>
												<span className="absolute right-0.5 sm:right-1 top-1/2 transform -translate-y-1/2 text-gray-500 text-[8px] sm:text-[10px] pointer-events-none">
													%
												</span>
											</div>
										</div>
									)}

									<div className="flex-1 min-w-0">
										<label htmlFor="unitary-value-input" className="block text-[9px] sm:text-[10px] font-medium text-gray-700 mb-1 text-center">
											Vr Unit
										</label>
										<FormField
											control={form.control}
											name="unitaryValue"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input
															id="unitary-value-input"
															{...field}
															autoComplete='off'
															disabled={isViewOrder || quantidade <= 0}
															inputMode="decimal"
															onFocus={(ev) => {
																ev.currentTarget.select()
															}}
															onChange={(ev) => {
																setValue(ev.target.value)
																handleChangeUnitaryValue(
																	field.onChange,
																	ev.target.value,
																)
															}}
															onBlur={(ev) => {
																field.onBlur()
																handleCommitUnitaryValue(ev)
															}}
															onKeyDown={handleCommitUnitaryValue}
															readOnly={isReadOnly || isCalculatingOrder}
															ref={shouldUseRef ? deferredUnitaryValueRef : null}
															className="h-7 sm:h-8 px-1 sm:px-2 bg-transparent border-0 border-b border-gray-300 rounded-none ring-0 text-center focus:ring-0 focus:border-blue-500 focus:outline-hidden text-xs font-semibold"
															value={value || formatMoneyWithTruncate(0)}
														/>
													</FormControl>
												</FormItem>
											)}
										/>
									</div>

									{isActiveButton !== false && (
										<div className="flex gap-0.5 sm:gap-1 shrink-0">
											<Button
												className="w-9 h-9 sm:w-7 sm:h-8 p-0 border border-gray-300 hover:bg-gray-50 rounded"
												variant="outline"
												disabled={isViewOrder}
												onClick={() => handleQuantityButton(false)}
												aria-label="Diminuir quantidade"
											>
												<Minus className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
											</Button>

											<Button
												className="bg-gray-700 hover:bg-gray-900 w-9 h-9 sm:w-7 sm:h-8 p-0 rounded"
												disabled={isViewOrder}
												onClick={() => {
													handleQuantityButton(true)
												}
												}
												aria-label="Aumentar quantidade"
											>
												<Plus className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white" />
											</Button>
										</div>
									)}
								</div>
							</Form>

							<ProductCardFooterComponent
								localTotalValue={localTotalValue}
								valorTotal={valorTotalItem}
								product={product}
							/>
						</div>
					</CardContent>
				</Card>
			</div >
		)
	}

	return (
		<Card
			className={cn(
				'w-full h-full',
				product.quantidade > 0 &&
				'border-green-400 shadow-xs',
			)}
		>
			<CardContent className="py-2 px-4 flex flex-col flex-1 h-full relative">
				{hasPromotion && (
					<div
						onKeyUp={(e) => {
							if (e.key === 'Enter' && canActivatePromotion()) {
								flagPromocao ? onDisablePromotion() : onActivatePromotion()
							}
						}}
						className={cn(
							"flex flex-row items-end cursor-pointer mb-2 mt-2",
							!canActivatePromotion() && "opacity-50 cursor-not-allowed"
						)}
						onClick={() => {
							if (canActivatePromotion()) {
								flagPromocao ? onDisablePromotion() : onActivatePromotion()
							} else {
								toast.info(`Quantidade mínima para promoção: ${qtdPromocao}`)
							}
						}}
					>
						<MdCurrencyExchange
							color={flagPromocao ? 'green' : 'gray'}
							className="mr-2"
							size={20}
						/>

						<div className="flex flex-col">
							<p className="text-xs font-semibold">
								{formattedPromotionValue}
							</p>
							<p className="text-[10px] text-gray-500">Qtd mín: {qtdPromocao}</p>
						</div>
					</div>
				)}

				<div className="flex flex-row flex-1 mt-auto justify-between relatie">
					<div className="flex flex-1 flex-col justify-center">
						<div className="flex flex-row mt-auto">
							<div
								onKeyUp={(e) => {
									if (e.key === 'Enter') {
										onOpenModalProductImage()
									}
								}}
								className="cursor-pointer flex items-center"
								onClick={onOpenModalProductImage}
							>
								<img
									src={imagem || '/sem-foto.jpeg'}
									alt="Imagem do produto"
									className="img object-scale-down justify-center h-16 w-16"
								/>
							</div>

							<div
								onKeyUp={(e) => {
									if (e.key === 'Enter') {
										e.stopPropagation()
										onChangeDefaultTabsProductDetails('dados')
										onOpenModalDetailsOfProduct()
									}
								}}
								className="w-[80%] cursor-pointer mb-2 text-xs"
								onClick={(e) => {
									e.stopPropagation()
									onChangeDefaultTabsProductDetails('dados')
									onOpenModalDetailsOfProduct()
								}}
							>
								<p className="font-semibold mb-2">
									{referenciaAfere} - {descricao}
								</p>

								{availableStock && (
									<div className="flex text-xs">
										<p className="mr-1">Qtd. Disponível:</p>
										<p>{qtdEstoque}</p>
									</div>
								)}
							{stockWarning && <p role="status" className="text-amber-700 text-xs">{stockWarning}</p>}
							</div>
						</div>
					</div>

					<div className="w-20 justify-center flex flex-col items-center">
						<div className="flex justify-center w-full text-center">
							{quantidade > 0 ? (
								<div className='flex flex-1 justify-center '>
									{fieldHeader?.fieldName === 'j_Plucro' ? (
										<p className={cn('text-sm w-full font-medium', getTextColorLL(statusLl))}>
											{Number(j_Plucro).toFixed(2)}
										</p>
									) : (
										<div>
											<ProductCardStatusComponent statusType={statusLl} />
										</div>
									)}
								</div>
							) : null}
							{flagPrecoFixo && isVisibleFixedValue && (
								<MdLock size={18} className="mt-8" />
							)}
						</div>
						<div>
							<p className="text-xs md:text-sm text-center font-semibold pt=0 md:pt-2">
								{deferredQuantity}
							</p>
							{ultimoPedido?.quantidade > 0 && (
								<p className="text-xs text-center ">{formattedLastBuyText}</p>
							)}
						</div>
					</div>
				</div>

				<Separator />

				<div className="mt-2 flex flex-row justify-between md:items-center">
					<div className="flex w-full justify-between items-center text-xs">
						<Form {...form}>
							<FormField
								control={form.control}
								name="quantity"
								render={({ field }) => (
									<FormItem className="mr-1 flex-1">
										<FormControl>
											<Input
												{...field}
												autoComplete='off'
												disabled={isViewOrder}
												onFocus={(ev) => {
													ev.currentTarget.select()
												}}
												onChange={(e) => {
													const inputValue = e.target.value;
													if (inputValue === '') {
														limitQuantityToStock(0);
														field.onChange('');
													} else {
														const value = Math.max(0, Number(inputValue) || 0);
														field.onChange(limitQuantityToStock(value));
													}
												}}
												onBlur={(ev) => {
													const currentValue = field.value;
													if (currentValue === undefined || Number.isNaN(Number(currentValue))) {
														field.onChange(0);
													} else {
														const value = Math.max(0, Number(currentValue));
														field.onChange(value);
													}
													field.onBlur();
													handleKeyUpOrBlurQuantity(ev);
												}}
												type="number"
												inputMode="numeric"
												min="0"
												onKeyUp={handleKeyUpOrBlurQuantity}
												ref={deferredQuantityInputRef}
												className={cn("h-8 p-1 border-0 border-b-2 rounded-none ring-0 text-center  focus:ring-offset-0 focus-visible:ring-0 focus:border-0 focus:border-b-2 focus:ring-0 focus:outline-hidden focus:outline-0", quantityError && 'border-red-500')}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
							<p className="px-1">x</p>

							<FormField
								control={form.control}
								name="unitaryValue"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormControl>
											<Input
												{...field}
												autoComplete='off'
												disabled={isViewOrder}
												inputMode="decimal"
												onBlur={(ev) => {
													field.onBlur()
													handleCommitUnitaryValue(ev)
												}}
												onFocus={(ev) => {
													ev.currentTarget.select()
												}}
												onChange={(ev) => {
													setValue(ev.target.value)
													handleChangeUnitaryValue(
														field.onChange,
														ev.target.value,
													)
												}}
												onKeyDown={handleCommitUnitaryValue}
												readOnly={isReadOnly || product?.flagPromocao || isCalculatingOrder}
												ref={shouldUseRef ? deferredUnitaryValueRef : null}
												className="h-8 p-1 border-0 border-b-2 rounded-none ring-0 text-center  focus:ring-offset-0 focus-visible:ring-0 focus:border-0 focus:border-b-2 focus:ring-0 focus:outline-hidden focus:outline-0"
												value={value || formatMoneyWithTruncate(0)}
											/>
										</FormControl>
									</FormItem>
								)}
							/>
						</Form>
					</div>
					<div className="  flex w-full justify-between items-center ">
						<p className="text-sm flex gap-2 mr-2">
							={' '}
							<span className="font-normal text-xs max-w-14">
								{localTotalValue !== undefined
									? formatMoneyWithTruncate(localTotalValue)
									: formatMoneyWithTruncate(valorTotalItem)}
							</span>
						</p>
						{isActiveButton !== false && (
							<div className="flex space-x-1 ">
								<Button
									className="w-8 h-8 p-2"
									variant="outline"
									disabled={isViewOrder || isCalculatingOrder}
									onClick={() => handleQuantityButton(false)}
								>
									<Minus />
								</Button>

								<Button
									className="bg-gray-700/90 hover:bg-gray-900/90 w-8 h-8 p-2"
									disabled={isViewOrder || isCalculatingOrder}
									onClick={() => handleQuantityButton(true)}
								>
									<Plus color="white" />
								</Button>
							</div>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	)
}
